import type { AxiosInstance, AxiosRequestConfig, Method, CancelTokenSource } from 'axios';
import axios from 'axios';

import { getFormatHeaders } from '../helpers';
import { HttpError } from '../http-error';
import type { IAdapter, IConfig, IResponse } from '../models';

export class AxiosAdapter implements IAdapter {
    private instance: AxiosInstance;

    private constructor({ baseUrl }: IConfig) {
        this.instance = axios.create({ baseURL: baseUrl });
    }

    static create(config: IConfig) {
        return new AxiosAdapter(config);
    }

    create(config: IConfig) {
        return new AxiosAdapter(config);
    }

    async send<T>(
        endpoint: string,
        { headers = {}, method = 'GET', signal, ...options }: RequestInit = {}
    ): Promise<IResponse<T>> {
        try {
            let source: CancelTokenSource | null = null;

            if (signal) {
                source = axios.CancelToken.source();

                signal.addEventListener('abort', () => {
                    source?.cancel();
                });
            }

            const config: AxiosRequestConfig = {
                url: endpoint,
                method: method as Method,
                withCredentials: options.credentials === 'include',
                cancelToken: source?.token,
                // TODO: Раскоментировать после апдейта axios
                // signal: options.signal ?? undefined,

                headers: getFormatHeaders(headers),
            };

            if (options.body) config.data = options.body;

            const response = await this.instance.request(config);

            return {
                headers: response?.headers,
                data: response?.data,
                status: response?.status,
                statusText: response?.statusText,
            };
        } catch (error: unknown) {
            if (!axios.isAxiosError(error)) throw error;

            const { data, headers: responseHeaders, status, statusText } = error.response ?? {};

            throw new HttpError(error.message, {
                data,
                statusCode: status,
                statusText,
                headers: responseHeaders,
            });
        }
    }
}
